<template>
    <section class="mt-4 rounded-xl border border-cyan-100 bg-cyan-50/40 p-3">
        <div class="flex flex-col gap-2">
            <div class="min-w-0">
                <p class="text-xs font-black uppercase tracking-wide text-cyan-800">Vídeo do teste</p>
                <p class="text-xs text-slate-500">{{ label }}</p>
                <p class="mt-1 text-[11px] font-semibold text-cyan-700">MP4, MOV ou WebM · até 2 GB · até 5 min</p>
            </div>
            <div v-if="loadingVideo" class="flex h-8 items-center justify-center">
                <span class="loading loading-spinner loading-sm text-cyan-600"></span>
            </div>
            <button v-else-if="!video" type="button" class="btn btn-sm w-full shrink-0 border-0 bg-cyan-600 text-white hover:bg-cyan-700" :disabled="busy()" @click="selectVideo">
                <svg v-if="checkingPlan" class="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                    <circle class="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3"></circle>
                    <path class="opacity-90" fill="currentColor" d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3Z"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 7.5a3 3 0 013-3h2.1l1.2-1.6A1.5 1.5 0 0111.25 2.25h1.5a1.5 1.5 0 011.2.65l1.2 1.6h2.1a3 3 0 013 3v8.25a3 3 0 01-3 3H6.75a3 3 0 01-3-3V7.5z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 11.25a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span v-if="!checkingPlan">Gravar ou enviar vídeo</span>
            </button>
        </div>

        <input ref="inputRef" type="file" class="hidden" accept="video/mp4,video/quicktime,video/webm,.mov" capture="environment" @change="onFileSelected">

        <div v-if="uploading" class="mt-3">
            <div class="mb-1 flex justify-between text-xs font-semibold text-cyan-700"><span>Enviando vídeo</span><span>{{ progress }}%</span></div>
            <progress class="progress progress-info h-2 w-full" :value="progress" max="100"></progress>
        </div>
        <div v-else-if="video && ['uploading', 'processing'].includes(video.status)" class="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
            <svg class="h-4 w-4 shrink-0 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3"></circle>
                <path class="opacity-90" fill="currentColor" d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3Z"></path>
            </svg>
            <span>{{ video.status === 'uploading' ? 'Vídeo aguardando processamento.' : 'Vídeo processando. Pode continuar preenchendo.' }}</span>
        </div>
        <div v-else-if="video && video.status === 'ready'" class="mt-3 flex flex-wrap gap-2">
            <button type="button" class="inline-flex h-9 items-center justify-center rounded-lg border border-cyan-200 bg-white px-3 text-xs font-bold text-cyan-700 transition hover:bg-cyan-100" @click="watchVideo">
                <svg xmlns="http://www.w3.org/2000/svg" class="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2"><path stroke-linecap="round" stroke-linejoin="round" d="m10.5 8.25 4.5 3.75-4.5 3.75v-7.5Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M21 12c0 4.142-4.03 7.5-9 7.5S3 16.142 3 12s4.03-7.5 9-7.5 9 3.358 9 7.5Z" /></svg>
                {{ playerUrl ? 'Ocultar vídeo' : 'Assistir vídeo' }}
            </button>
            <button type="button" class="inline-flex h-9 items-center justify-center rounded-lg border border-transparent px-3 text-xs font-bold text-slate-500 transition hover:border-red-100 hover:bg-red-50 hover:text-red-600" @click="confirmRemoveVideo">
                <svg xmlns="http://www.w3.org/2000/svg" class="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-0.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673A2.25 2.25 0 0 1 15.916 21.75H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.134-2.034-2.172a48.67 48.67 0 0 0-3.364 0C9.41 2.343 8.5 3.297 8.5 4.477v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                Excluir
            </button>
        </div>
        <div v-else-if="video && video.status === 'failed'" class="mt-3 flex flex-wrap items-center gap-2 text-xs text-red-600">
            <span>Erro ao processar vídeo.</span>
            <button type="button" class="btn btn-xs border-red-200 bg-white text-red-600" @click="retryVideo">Tentar novamente</button>
        </div>
        <p v-else-if="errorMessage" class="mt-3 text-xs text-red-600">{{ errorMessage }}</p>

        <video v-if="playerUrl" class="mt-3 max-h-80 w-full rounded-lg bg-black" controls autoplay muted playsinline preload="metadata" :src="playerUrl"></video>

        <dialog ref="upgradeModalRef" class="modal">
            <div class="modal-box max-w-lg overflow-hidden rounded-[28px] border-0 bg-white p-0 shadow-2xl">
                <div class="relative overflow-hidden bg-gradient-to-br from-amber-300 via-amber-500 to-orange-600 px-6 pb-24 pt-7 text-slate-950">
                    <div class="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-yellow-100/40 blur-2xl"></div>
                    <div class="absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-orange-900/20 blur-2xl"></div>
                    <div class="relative flex items-start justify-between gap-4">
                        <div>
                            <div class="inline-flex items-center gap-2 rounded-full border border-amber-950/10 bg-white/25 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="m12 2 2.28 4.62 5.1.74-3.69 3.6.87 5.08L12 13.64l-4.56 2.4.87-5.08-3.69-3.6 5.1-.74L12 2Z" /></svg>
                                Plano Premium
                            </div>
                            <h3 class="mt-4 text-3xl font-black leading-none tracking-tight">Dê movimento<br>à avaliação.</h3>
                            <p class="mt-3 max-w-sm text-sm font-semibold leading-5 text-amber-950/80">Registre execução real de cada teste. Mais contexto, análise mais precisa, acompanhamento melhor.</p>
                        </div>
                        <button type="button" class="btn btn-circle btn-sm border-0 bg-white/25 text-slate-900 hover:bg-white/45" aria-label="Fechar" @click="closeUpgradeModal">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="m6 6 12 12M18 6 6 18" /></svg>
                        </button>
                    </div>
                    <div class="absolute bottom-5 left-6 right-6 flex items-center gap-3 rounded-2xl border border-white/70 bg-white p-3 shadow-xl">
                        <div class="flex h-12 w-16 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-800 to-slate-950 text-amber-300 shadow-inner">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5V6.75A2.25 2.25 0 0 0 12.75 4.5h-6A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25h6A2.25 2.25 0 0 0 15 17.25V13.5l4.5 2.25v-7.5L15 10.5Z" /></svg>
                        </div>
                        <div class="min-w-0">
                            <div class="text-xs font-black uppercase tracking-wide text-slate-700">Vídeos por teste</div>
                            <div class="mt-0.5 text-xs leading-4 text-slate-500">MP4, MOV ou WebM · até 2 GB · até 5 min</div>
                        </div>
                    </div>
                </div>

                <div class="px-6 pb-6 pt-6">
                    <div class="grid grid-cols-3 gap-2">
                        <div class="rounded-xl bg-amber-50 px-2 py-3 text-center">
                            <div class="text-lg font-black text-amber-700">70</div>
                            <div class="text-[10px] font-bold uppercase leading-3 text-slate-500">Relatórios IA/mês</div>
                        </div>
                        <div class="rounded-xl bg-amber-50 px-2 py-3 text-center">
                            <div class="text-lg font-black text-amber-700">40</div>
                            <div class="text-[10px] font-bold uppercase leading-3 text-slate-500">ACIAS/mês</div>
                        </div>
                        <div class="rounded-xl bg-amber-50 px-2 py-3 text-center">
                            <div class="text-lg font-black text-amber-700">15</div>
                            <div class="text-[10px] font-bold uppercase leading-3 text-slate-500">Dispositivos</div>
                        </div>
                    </div>

                    <div class="mt-5 flex items-end justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <div>
                            <div class="text-xs font-bold uppercase tracking-wide text-slate-400">Plano Premium</div>
                            <div class="mt-1 text-sm font-semibold text-slate-600">Tudo do Standard + vídeos</div>
                        </div>
                        <div class="text-right"><span class="text-xs font-bold text-slate-400">por mês</span><div class="text-2xl font-black tracking-tight text-slate-900">R$ 79,90</div></div>
                    </div>

                    <p v-if="upgradeError" class="mt-3 text-xs font-semibold text-red-600">{{ upgradeError }}</p>
                    <button type="button" class="btn mt-4 w-full border-0 bg-gradient-to-r from-amber-500 to-orange-600 font-black text-white shadow-lg shadow-amber-500/25 hover:from-amber-600 hover:to-orange-700" :disabled="upgrading" @click="upgrade">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4"><path stroke-linecap="round" stroke-linejoin="round" d="m13.5 4.5 6 7.5-6 7.5M4.5 12h14.25" /></svg>
                        {{ upgrading ? 'Preparando upgrade…' : 'Desbloquear Plano Premium' }}
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm mt-2 w-full text-slate-500" :disabled="upgrading" @click="closeUpgradeModal">Continuar sem vídeos</button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button @click="closeUpgradeModal">close</button></form>
        </dialog>

        <dialog ref="deleteModalRef" class="modal">
            <div class="modal-box max-w-md rounded-3xl p-6">
                <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-0.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673A2.25 2.25 0 0 1 15.916 21.75H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.134-2.034-2.172a48.67 48.67 0 0 0-3.364 0C9.41 2.343 8.5 3.297 8.5 4.477v.916" /></svg>
                </div>
                <h3 class="mt-4 text-xl font-black text-slate-800">Excluir este vídeo?</h3>
                <p class="mt-2 text-sm leading-6 text-slate-600">O vídeo processado será removido permanentemente. Esta ação não pode ser desfeita.</p>
                <div class="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button type="button" class="btn btn-sm" :disabled="deleting" @click="closeDeleteModal">Cancelar</button>
                    <button type="button" class="btn btn-sm border-0 bg-red-600 text-white hover:bg-red-700" :disabled="deleting" @click="removeVideo">{{ deleting ? 'Excluindo…' : 'Excluir vídeo' }}</button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button @click="closeDeleteModal">close</button></form>
        </dialog>
    </section>
</template>

<script>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { SubscriptionService, VideoService } from '@/services/Entity';
import AuthService from '@/services/AuthService';

const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024;
const MAX_VIDEO_DURATION_SECONDS = 5 * 60;
let planClaimRefreshPromise = null;

function cachedPlan() {
    return AuthService.getAuthInfo()?.plan || null;
}

function refreshPlanClaim() {
    if (!planClaimRefreshPromise) {
        planClaimRefreshPromise = AuthService.refreshSession()
            .then(() => cachedPlan())
            .finally(() => { planClaimRefreshPromise = null; });
    }
    return planClaimRefreshPromise;
}

export default {
    name: 'RecordTestVideo',
    props: {
        recordUuid: { type: String, default: null },
        patientUuid: { type: String, required: true },
        testKey: { type: String, required: true },
        side: { type: String, default: 'both' },
        label: { type: String, required: true }
    },
    emits: ['ensure-record'],
    setup(props, { emit }) {
        const inputRef = ref(null);
        const upgradeModalRef = ref(null);
        const deleteModalRef = ref(null);
        const video = ref(null);
        const loadingVideo = ref(false);
        const uploading = ref(false);
        const progress = ref(0);
        const playerUrl = ref(null);
        const errorMessage = ref('');
        const upgrading = ref(false);
        const upgradeError = ref('');
        const checkingPlan = ref(false);
        const deleting = ref(false);
        let refreshTimer = null;

        const busy = () => uploading.value || upgrading.value || checkingPlan.value;

        async function loadVideo(force = false) {
            if (!props.recordUuid) return;
            loadingVideo.value = true;
            try {
                const { data } = await VideoService.listByRecord(props.recordUuid, { force });
                const matchingVideos = (data.items || [])
                    .filter(item => item.test_key === props.testKey)
                    .sort((a, b) => Number(b.attempt || 0) - Number(a.attempt || 0));
                // Registros antigos podem ter side/attempt diferente. test_key é
                // referência canônica do campo e nunca deve ocultar vídeo salvo.
                video.value = matchingVideos.find(item => item.side === props.side && item.attempt === 1) || matchingVideos[0] || null;
                if (video.value && ['uploading', 'processing'].includes(video.value.status)) scheduleRefresh();
            } catch (error) {
                if (error?.response?.data?.error !== 'premium_required') errorMessage.value = 'Não foi possível consultar vídeo.';
            } finally {
                loadingVideo.value = false;
            }
        }

        function scheduleRefresh() {
            clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => loadVideo(true), 15000);
        }

        function showUpgradeModal() {
            upgradeError.value = '';
            upgradeModalRef.value?.showModal();
        }

        function closeUpgradeModal() {
            upgradeModalRef.value?.close();
        }

        async function upgrade() {
            upgrading.value = true;
            upgradeError.value = '';
            try {
                await SubscriptionService.changePlan('premium');
                await AuthService.refreshSession();
                closeUpgradeModal();
            } catch (error) {
                upgradeError.value = error?.response?.data?.message || 'Upgrade não disponível. Verifique sua assinatura.';
            } finally {
                upgrading.value = false;
            }
        }

        async function selectVideo() {
            if (busy()) return;
            errorMessage.value = '';
            let plan = cachedPlan();

            // Sessões antigas não possuem claim de plano. Renova uma única vez
            // e passa a usar JWT assinado nos próximos cliques.
            if (!plan) {
                checkingPlan.value = true;
                try {
                    plan = await refreshPlanClaim();
                } catch (error) {
                    errorMessage.value = 'Não foi possível validar acesso a vídeos.';
                    return;
                } finally {
                    checkingPlan.value = false;
                }
            }

            if (plan !== 'premium') {
                showUpgradeModal();
                return;
            }
            inputRef.value?.click();
        }

        async function ensureRecord() {
            if (props.recordUuid) return props.recordUuid;
            return await new Promise(resolve => emit('ensure-record', resolve));
        }

        async function onFileSelected(event) {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            if (file.size > MAX_VIDEO_BYTES) { errorMessage.value = 'Vídeo deve ter até 2 GB.'; return; }
            const mime = normalizeVideoMime(file);
            if (!mime) { errorMessage.value = 'Use MP4, MOV ou WebM.'; return; }
            try {
                const duration = await getVideoDuration(file);
                if (duration > MAX_VIDEO_DURATION_SECONDS) { errorMessage.value = 'Vídeo deve ter até 5 minutos.'; return; }
            } catch (error) {
                errorMessage.value = 'Não foi possível validar duração do vídeo.';
                return;
            }
            const recordUuid = await ensureRecord();
            if (!recordUuid) { errorMessage.value = 'Salve avaliação antes de enviar vídeo.'; return; }
            uploading.value = true;
            progress.value = 0;
            errorMessage.value = '';
            try {
                const { data: init } = await VideoService.initUpload({
                    record_uuid: recordUuid, patient_uuid: props.patientUuid, test_key: props.testKey,
                    side: props.side, attempt: 1, filename: file.name, mime, size: file.size
                });
                await VideoService.queueUpload(() => putFile(init.upload_url, file, mime, value => { progress.value = value; }));
                const { data: completed } = await VideoService.completeUpload(init.uuid, { size: file.size });
                video.value = { uuid: init.uuid, test_key: props.testKey, side: props.side, attempt: 1, status: completed.status };
                VideoService.invalidateListByRecord(recordUuid);
                scheduleRefresh();
            } catch (error) {
                const payload = error?.response?.data;
                if (payload?.error === 'premium_required') showUpgradeModal();
                else if (payload?.error === 'video_exists') {
                    video.value = payload.video || video.value;
                    await loadVideo();
                    if (!video.value) errorMessage.value = 'Já existe vídeo para este teste. Atualize a página para ver o status.';
                } else errorMessage.value = payload?.message || 'Não foi possível enviar vídeo.';
            } finally {
                uploading.value = false;
            }
        }

        async function watchVideo() {
            if (playerUrl.value) { playerUrl.value = null; return; }
            try {
                const { data } = await VideoService.getPlaybackUrl(video.value.uuid);
                playerUrl.value = data.url;
            } catch (error) {
                errorMessage.value = error?.response?.data?.message || 'Não foi possível abrir vídeo.';
            }
        }

        async function retryVideo() {
            try {
                const { data } = await VideoService.retry(video.value.uuid);
                video.value = { ...video.value, status: data.status };
                scheduleRefresh();
            } catch (error) {
                errorMessage.value = error?.response?.data?.message || 'Não foi possível tentar novamente.';
            }
        }

        function confirmRemoveVideo() {
            deleteModalRef.value?.showModal();
        }

        function closeDeleteModal() {
            deleteModalRef.value?.close();
        }

        async function removeVideo() {
            if (!video.value || deleting.value) return;
            deleting.value = true;
            try {
                await VideoService.destroy(video.value.uuid);
                VideoService.invalidateListByRecord(props.recordUuid);
                video.value = null;
                playerUrl.value = null;
                closeDeleteModal();
            } catch (error) {
                errorMessage.value = 'Não foi possível excluir vídeo.';
            } finally {
                deleting.value = false;
            }
        }

        function handleBeforeUnload(event) {
            if (!uploading.value) return;
            event.preventDefault();
            event.returnValue = '';
        }

        onMounted(() => {
            loadVideo();
            window.addEventListener('beforeunload', handleBeforeUnload);
        });
        onBeforeUnmount(() => {
            clearTimeout(refreshTimer);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        });
        watch(() => props.recordUuid, loadVideo);

        return { inputRef, upgradeModalRef, deleteModalRef, video, loadingVideo, uploading, progress, playerUrl, errorMessage, upgrading, upgradeError, checkingPlan, deleting, busy, selectVideo, onFileSelected, watchVideo, retryVideo, confirmRemoveVideo, removeVideo, upgrade, closeUpgradeModal, closeDeleteModal };
    }
};

function normalizeVideoMime(file) {
    if (['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type)) return file.type;
    if (/\.mov$/i.test(file.name || '')) return 'video/quicktime';
    return null;
}

function getVideoDuration(file) {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        const cleanup = () => URL.revokeObjectURL(url);
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            const duration = video.duration;
            cleanup();
            Number.isFinite(duration) && duration > 0 ? resolve(duration) : reject(new Error('invalid_duration'));
        };
        video.onerror = () => { cleanup(); reject(new Error('invalid_video')); };
        video.src = url;
    });
}

function putFile(url, file, mime, onProgress) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.upload.onprogress = event => {
            if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
        };
        request.onerror = () => reject(new Error('upload_failed'));
        request.onload = () => request.status >= 200 && request.status < 300 ? resolve() : reject(new Error('upload_failed'));
        request.open('PUT', url);
        request.setRequestHeader('Content-Type', mime);
        request.send(file);
    });
}
</script>
